<template>
  <v-container fill-height fluid>
    <v-dialog v-model="newUserDialog" width="500" persistent>
      <v-card dark>
        <v-card-title class="headline">
          Password change required
        </v-card-title>

        <v-card-text class="mt-2">
          This is your first time logging in. Set a new password to continue.
          <v-text-field
            v-model="newPassword"
            class="mt-4"
            prepend-icon="mdi-lock"
            label="New password"
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            outlined
            dense
            :disabled="loading"
            @click:append="showNewPassword = !showNewPassword"
            @input="clearPasswordMismatchError"
            @keydown.enter="submitNewPassword"
          />
          <v-text-field
            v-model="confirmPassword"
            prepend-icon="mdi-lock-check"
            label="Confirm password"
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            outlined
            dense
            :disabled="loading"
            @click:append="showConfirmPassword = !showConfirmPassword"
            @input="clearPasswordMismatchError"
            @keydown.enter="submitNewPassword"
          />
          <div v-if="newPasswordError" class="error--text text-body-2 mt-1">
            {{ newPasswordError }}
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="loading" @click="cancelNewPassword">
            Cancel
          </v-btn>
          <v-btn
            color="info"
            text
            :loading="loading"
            @click="submitNewPassword"
          >
            Set Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-layout align-center justify-center>
      <v-flex xs12 sm8 md4>
        <v-card outlined dark class="elevation-12">
          <v-toolbar extended extension-height="30" dark color="secondary">
            <v-toolbar-title>
              <v-img
                max-height="150"
                max-width="130"
                src="@/assets/eventPosLogo.png"
              />
            </v-toolbar-title>
            <template v-slot:extension>
              <span class="text-caption">
                <v-icon small class="mr-1">mdi-information</v-icon>
                Please enter your login credentials
              </span>
            </template>
          </v-toolbar>

          <v-card-text>
            <v-form>
              <v-text-field v-model="username" prepend-icon="mdi-account" name="username" label="Username" type="text" @keydown.enter="loginPressed"></v-text-field>
              <v-text-field v-model="password" id="password" prepend-icon="mdi-lock" name="password" label="Password" type="password" @keydown.enter="loginPressed"></v-text-field>
            </v-form>
            <a id="forgotPassword" class="text-caption info--text" @click="gotoCognitoHostedUI('forgotPassword')"> Forgot your password? </a>

            <div class="my-6 text-body-2" v-if="errorMessage">
              <v-icon medium color="error">mdi-alert</v-icon> {{errorMessage}}
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn large class="ml-2 mb-2" color="secondary" @click="loginPressed" :loading="loading">
              <v-icon left dense >mdi-login</v-icon>
              Login</v-btn>
          </v-card-actions>

        </v-card>
        <span class="white--text text-caption">
          eventPOS Portal 2.0 (© Technical Services Group Pty Ltd)
        </span>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
/* eslint-disable no-unused-vars */

export default {
  data: () => ({
    newUserDialog: false,
    challengeUser: null,
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    showNewPassword: false,
    showConfirmPassword: false,
    newPasswordError: '',
    loading: false,
    errorMessage: ''
  }),
  created() {
    this.$authentication.getAuth().currentAuthenticatedUser().then(user => {
      this.$dashboardAPI.setHeader(user.signInUserSession.idToken.jwtToken)
      this.$router.push({
        path: this.$route.query.redirect || '/'
      })
    }, err => {
      // No user has logged in
    })
  },
  methods: {
    finishLogin(user) {
      this.username = ''
      this.password = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.challengeUser = null
      this.newUserDialog = false
      this.loading = false
      this.$dashboardAPI.setHeader(user.signInUserSession.idToken.jwtToken)
      this.$router.push({
        path: this.$route.query.redirect || '/'
      })
    },
    loginPressed() {
      this.loading = true
      this.errorMessage = ''
      this.$authentication.signIn(this.username, this.password).then(response => {
        if (response.challengeName == "NEW_PASSWORD_REQUIRED") {
          this.password = ''
          this.loading = false
          this.challengeUser = response
          this.newPassword = ''
          this.confirmPassword = ''
          this.newPasswordError = ''
          this.newUserDialog = true
        } else {
          this.finishLogin(response)
        }
      }).catch(err => {
        this.loading = false
        this.errorMessage = err.message || err
        this.password = ''
      })
    },
    cancelNewPassword() {
      this.newUserDialog = false
      this.challengeUser = null
      this.newPassword = ''
      this.confirmPassword = ''
      this.showNewPassword = false
      this.showConfirmPassword = false
      this.newPasswordError = ''
      this.$authentication.signOut().catch(() => {})
    },
    clearPasswordMismatchError() {
      if (this.newPasswordError === 'Passwords do not match') {
        if (this.newPassword === this.confirmPassword) {
          this.newPasswordError = ''
        }
        return
      }
      if (
        this.newPasswordError === 'Password must be at least 8 characters' &&
        this.newPassword.length >= 8
      ) {
        this.newPasswordError = ''
      }
    },
    submitNewPassword() {
      this.newPasswordError = ''
      if (!this.newPassword || this.newPassword.length < 8) {
        this.newPasswordError = 'Password must be at least 8 characters'
        return
      }
      if (this.newPassword !== this.confirmPassword) {
        this.newPasswordError = 'Passwords do not match'
        return
      }
      if (!this.challengeUser) {
        this.newPasswordError = 'Session expired — log in again with the temporary password'
        return
      }
      this.loading = true
      this.$authentication
        .completeNewPassword(this.challengeUser, this.newPassword)
        .then((user) => this.finishLogin(user))
        .catch((err) => {
          this.loading = false
          this.newPasswordError = err.message || String(err)
        })
    },
    gotoCognitoHostedUI(type) {
      // Forgot-password still uses Hosted UI (callback must be allowed in Cognito)
      var url = `${process.env.VUE_APP_COGNITO_URL}/${type}?client_id=${(process.env.VUE_APP_USER_POOL_CLIENT_ID || '').trim()}&response_type=token&scope=email+openid&redirect_uri=${(process.env.VUE_APP_COGNITO_REDIRECT_URL || '').trim()}`
      window.location.href = url;
    }
  }
}
</script>

<style scoped>
#forgotPassword:hover {
  text-decoration: underline
}
</style>
